// FILE: src/pages/EmployeeDashboard.jsx
import React, { useEffect, useState } from 'react';
import { auth, firestore } from '../firebase/config';
import { doc, getDoc, setDoc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { useToast } from '../components/ToastContext';
import LoadingSpinner from '../components/LoadingSpinner';
import ChatComponent from '../components/ChatComponent';
import Notes from '../components/Notes';
import { getPakistanTime, getPakistanDate, calculateDuration } from '../utils/timeUtils';

function daysSince(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date)) return '-';
  const now = new Date();
  date.setHours(0,0,0,0);
  now.setHours(0,0,0,0);
  const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  return diff;
}

function EmployeeDashboard() {
  console.log('EmployeeDashboard component mounted');
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attendanceToday, setAttendanceToday] = useState(null);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [checkInLoading, setCheckInLoading] = useState(false);
  const [checkOutLoading, setCheckOutLoading] = useState(false);
  const navigate = useNavigate();
  const today = getPakistanDate();
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    const fetchEmployee = async () => {
      // Check for custom authentication first
      const customUser = localStorage.getItem('customUser');
      const customRole = localStorage.getItem('userRole');
      
      console.log('EmployeeDashboard: Checking custom auth:', { customUser: !!customUser, customRole });
      
      let userUid = null;
      
      if (customUser && customRole) {
        const parsedUser = JSON.parse(customUser);
        userUid = parsedUser.uid;
        console.log('EmployeeDashboard: Using custom auth, UID:', userUid);
      } else {
        // Fallback to Firebase Auth
        const user = auth.currentUser;
        if (!user) {
          console.log('EmployeeDashboard: No user found, redirecting to login');
          navigate('/login');
          return;
        }
        userUid = user.uid;
        console.log('EmployeeDashboard: Using Firebase auth, UID:', userUid);
      }
      
      try {
        const empDoc = await getDoc(doc(firestore, 'employees', userUid));
        if (empDoc.exists()) {
          const employeeData = empDoc.data();
          console.log('EmployeeDashboard: Fetched employee data:', employeeData);
          console.log('EmployeeDashboard: dateJoining field:', employeeData.dateJoining);
          console.log('EmployeeDashboard: joiningDate field:', employeeData.joiningDate);
          console.log('EmployeeDashboard: dateOfJoining field:', employeeData.dateOfJoining);
          console.log('EmployeeDashboard: joining field:', employeeData.joining);
          console.log('EmployeeDashboard: All available fields:', Object.keys(employeeData));
          
          // Check for any field containing 'date' or 'join'
          const dateFields = Object.keys(employeeData).filter(key => 
            key.toLowerCase().includes('date') || key.toLowerCase().includes('join')
          );
          console.log('EmployeeDashboard: Date/Join related fields:', dateFields);
          dateFields.forEach(field => {
            console.log(`EmployeeDashboard: ${field}:`, employeeData[field]);
          });
          setEmployee(employeeData);
        }
        // Fetch today's attendance
        const todayRef = doc(firestore, 'attendance', `${userUid}_${today}`);
        const snap = await getDoc(todayRef);
        if (snap.exists()) {
          setAttendanceToday(snap.data());
        }
        // Fetch attendance history
        const q = query(collection(firestore, 'attendance'), where('uid', '==', userUid));
        const querySnapshot = await getDocs(q);
        const history = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => a.date < b.date ? 1 : -1);
        setAttendanceHistory(history);
      } catch (err) {
        console.error('Error fetching employee data:', err);
      }
      setLoading(false);
    };
    fetchEmployee();
  }, [navigate, today]);

  const handleCheckIn = async () => {
    setCheckInLoading(true);
    try {
      // Get user UID from custom auth or Firebase auth
      const customUser = localStorage.getItem('customUser');
      let userUid;
      
      if (customUser) {
        const parsedUser = JSON.parse(customUser);
        userUid = parsedUser.uid;
      } else {
        const user = auth.currentUser;
        if (!user) {
          showError('User not authenticated');
          setCheckInLoading(false);
          return;
        }
        userUid = user.uid;
      }
      
      // Use Pakistan Standard Time with 12-hour format
      const time = getPakistanTime();
      const date = getPakistanDate();
      const ref = doc(firestore, 'employeeAttendance', `${userUid}_${date}`);
      await setDoc(ref, { uid: userUid, date: date, checkIn: time, userType: 'employee' });
      setAttendanceToday({ uid: userUid, date: date, checkIn: time });
      setAttendanceHistory(prev => [{ uid: userUid, date: date, checkIn: time }, ...prev]);
      showSuccess(`Checked in at ${time}`);
    } catch (err) {
      showError('Check-in failed. Try again.');
    }
    setCheckInLoading(false);
  };

  const handleCheckOut = async () => {
    setCheckOutLoading(true);
    try {
      // Get user UID from custom auth or Firebase auth
      const customUser = localStorage.getItem('customUser');
      let userUid;
      
      if (customUser) {
        const parsedUser = JSON.parse(customUser);
        userUid = parsedUser.uid;
      } else {
        const user = auth.currentUser;
        if (!user) {
          showError('User not authenticated');
          setCheckOutLoading(false);
          return;
        }
        userUid = user.uid;
      }
      
      // Use Pakistan Standard Time with 12-hour format
      const time = getPakistanTime();
      
      // Calculate duration with improved logic for 12-hour format
      let duration = '';
      if (attendanceToday && attendanceToday.checkIn) {
        duration = calculateDuration(attendanceToday.checkIn, time);
      }
      
      const ref = doc(firestore, 'employeeAttendance', `${userUid}_${today}`);
      await setDoc(ref, { ...attendanceToday, checkOut: time, duration, userType: 'employee' });
      setAttendanceToday(prev => ({ ...prev, checkOut: time, duration }));
      setAttendanceHistory(prev => prev.map(a => a.date === today ? { ...a, checkOut: time, duration } : a));
      showSuccess(`Checked out at ${time}`);
    } catch (err) {
      showError('Check-out failed. Try again.');
    }
    setCheckOutLoading(false);
  };



  // Helper function to find joining date field
  const getJoiningDate = (employeeData) => {
    if (!employeeData) return '-';
    
    // Common field names for joining date
    const possibleFields = [
      'dateJoining', 'joiningDate', 'dateOfJoining', 'joining', 
      'joinDate', 'startDate', 'hireDate', 'employmentDate',
      'dateOfEmployment', 'startDate', 'dateStarted'
    ];
    
    // Check for exact matches first
    for (const field of possibleFields) {
      if (employeeData[field]) {
        return employeeData[field];
      }
    }
    
    // Check for fields containing 'date' or 'join'
    const allFields = Object.keys(employeeData);
    const dateFields = allFields.filter(key => 
      key.toLowerCase().includes('date') || key.toLowerCase().includes('join')
    );
    
    for (const field of dateFields) {
      if (employeeData[field]) {
        return employeeData[field];
      }
    }
    
    return '-';
  };

  if (loading) {
    return <LoadingSpinner />;
  }
  if (!employee) {
    return <div className="min-h-screen flex items-center justify-center text-red-600">Employee data not found.</div>;
  }

  const hasCheckedIn = attendanceToday && attendanceToday.checkIn;
  const hasCheckedOut = attendanceToday && attendanceToday.checkOut;

  const canCheckIn = !attendanceToday || !attendanceToday.checkIn;
  const canCheckOut = attendanceToday && attendanceToday.checkIn && !attendanceToday.checkOut;

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-purple-50 to-orange-50 flex flex-col">
      {/* Chat Component for Employees */}
      <ChatComponent userRole="employee" userId={employee?.uid} userName={employee?.fullName} />
      
      {/* Navbar/Header with Enhanced Animations */}
      <header className="w-full bg-gradient-to-r from-orange-600 via-purple-600 to-orange-600 shadow-2xl py-3 md:py-4 px-3 md:px-6 flex items-center justify-between sticky top-0 z-20 rounded-b-3xl animate-fade-in overflow-hidden relative">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-r from-orange-400/20 via-purple-400/20 to-orange-400/20 animate-pulse"></div>
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-400 via-purple-400 to-orange-400 animate-pulse"></div>
        
        <div className="flex items-center gap-2 md:gap-3 animate-slide-in-left relative z-10">
          <div className="relative">
            <img src="/DSH.png" alt="Logo" className="w-8 h-8 md:w-12 md:h-12 rounded-xl shadow-lg animate-pulse" />
            <div className="absolute -top-1 -right-1 w-3 h-3 md:w-4 md:h-4 bg-green-400 rounded-full animate-ping"></div>
          </div>
          <div className="flex flex-col">
            <span className="text-lg md:text-2xl lg:text-3xl font-bold text-white tracking-wide animate-fade-in-delay drop-shadow-lg">
              Digital Skills House
            </span>
            <span className="text-xs md:text-sm text-blue-100 font-medium animate-fade-in-delay-2">
              {employee?.role === 'intern' ? 'Intern Portal' : 'Employee Portal'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 md:gap-4 animate-slide-in-right relative z-10">
          <div className="bg-white/20 backdrop-blur-sm rounded-lg px-2 md:px-4 py-1 md:py-2 animate-fade-in-delay-2">
            <span className="text-sm md:text-lg font-semibold text-white hidden sm:block">
              Welcome, {employee.fullName}
            </span>
            <span className="text-xs font-semibold text-white sm:hidden">
              {employee.fullName?.split(' ')[0]}
            </span>
          </div>
          <button
            onClick={async () => { 
              // Clear custom authentication
              localStorage.removeItem('customUser');
              localStorage.removeItem('userRole');
              
              // Sign out from Firebase Auth
              await signOut(auth); 
              navigate('/login'); 
            }}
            className="flex items-center px-2 md:px-4 py-1 md:py-2 bg-red-500/90 backdrop-blur-sm text-white rounded-lg shadow-lg hover:bg-red-600 transition-all duration-300 transform hover:scale-105 animate-fade-in-delay-3 border border-red-400/50"
          >
            <svg className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H7a2 2 0 01-2-2V7a2 2 0 012-2h4a2 2 0 012 2v1" />
            </svg>
            <span className="text-sm md:text-base">Logout</span>
          </button>
        </div>
      </header>

      <main className="flex-1 w-full max-w-6xl mx-auto py-8 px-2 md:px-8">
        {/* Admin Message with Animation */}
        {employee?.message && (
          <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800 p-4 mb-6 rounded shadow animate-bounce relative max-w-md mx-auto">
            <button 
                             onClick={async () => {
                 try {
                   // Get user UID from custom auth or Firebase auth
                   const customUser = localStorage.getItem('customUser');
                   let userUid;
                   
                   if (customUser) {
                     const parsedUser = JSON.parse(customUser);
                     userUid = parsedUser.uid;
                   } else {
                     const user = auth.currentUser;
                     if (!user) return;
                     userUid = user.uid;
                   }
                   
                   const employeeDocRef = doc(firestore, 'employees', userUid);
                   await updateDoc(employeeDocRef, { message: null });
                   setEmployee(prev => ({ ...prev, message: null }));
                 } catch (err) {
                   console.error('Error dismissing message:', err);
                 }
               }}
              className="absolute top-2 right-2 text-yellow-600 hover:text-yellow-800 font-bold text-lg transition-colors duration-200"
            >
              ×
            </button>
            <strong>Admin Message:</strong>
            <div className="mt-1 whitespace-pre-line pr-6">{employee.message}</div>
          </div>
        )}

        {/* Single Unified Card with Animation */}
        <div className="bg-white/80 rounded-2xl shadow-xl p-6 animate-fade-in-up">
          {/* Section 1: Profile and Financial Summary */}
          <div className="flex flex-col md:flex-row items-center gap-8 mb-8">
            <div className="flex flex-col items-center md:items-start flex-1 animate-slide-in-left">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-100 to-purple-100 rounded-full flex items-center justify-center mb-2 shadow animate-pulse">
                <svg className="w-10 h-10 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
              <div className="text-xl font-bold text-gray-800">{employee.fullName}</div>
              <div className="text-gray-500 text-sm">
                Joining: {getJoiningDate(employee)}
            </div>
              <div className="text-purple-600 font-semibold flex items-center mt-1">
                <svg className="w-5 h-5 mr-1 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2V6" />
              </svg>
                {employee.role}
              </div>
            </div>
            <div className="flex-1 flex flex-row justify-center gap-4 animate-slide-in-right">
              <div className="flex flex-col items-center bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl px-6 py-4 shadow hover:shadow-lg transition-all duration-300 transform hover:scale-105">
                <span className="text-xs text-gray-600 mb-1">Total Salary</span>
                <span className="text-xl font-bold text-orange-700">{employee.totalSalary}</span>
          </div>
              <div className="flex flex-col items-center bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl px-6 py-4 shadow hover:shadow-lg transition-all duration-300 transform hover:scale-105">
                <span className="text-xs text-gray-600 mb-1">Paid Salary</span>
                <span className="text-xl font-bold text-purple-700">{employee.paidSalary}</span>
            </div>
              <div className="flex flex-col items-center bg-gradient-to-br from-orange-100 to-purple-100 rounded-xl px-6 py-4 shadow hover:shadow-lg transition-all duration-300 transform hover:scale-105">
                <span className="text-xs text-gray-600 mb-1">Remaining Salary</span>
                <span className="text-xl font-bold bg-gradient-to-r from-orange-600 to-purple-600 bg-clip-text text-transparent">{employee.remainingSalary}</span>
            </div>
            </div>
          </div>

          {/* Section 2: Today's Attendance */}
          <div className="mb-8 animate-fade-in-delay">
            <h3 className="text-lg font-semibold mb-4">Today's Attendance ({today})</h3>
            <div className="flex justify-between items-center mb-4">
              <div className="flex gap-4">
            {canCheckIn && (
                  <button 
                    onClick={handleCheckIn} 
                    disabled={checkInLoading} 
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg shadow hover:bg-purple-700 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 animate-pulse"
                  >
                {checkInLoading ? 'Checking In...' : 'Check In'}
              </button>
            )}
              </div>
              <div className="flex gap-4">
            {canCheckOut && (
                  <button 
                    onClick={handleCheckOut} 
                    disabled={checkOutLoading} 
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg shadow hover:bg-purple-700 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 animate-pulse"
                  >
                {checkOutLoading ? 'Checking Out...' : 'Check Out'}
              </button>
            )}
              </div>
            </div>
            {attendanceToday && attendanceToday.checkIn && attendanceToday.checkOut && (
              <div className="text-green-600 font-semibold animate-bounce text-center">
                You have completed check-in and check-out for today.
              </div>
            )}
            <div className="text-sm">
            <span className="font-semibold">Check In:</span> {attendanceToday && attendanceToday.checkIn ? attendanceToday.checkIn : '--'}<br />
            <span className="font-semibold">Check Out:</span> {attendanceToday && attendanceToday.checkOut ? attendanceToday.checkOut : '--'}
          </div>
        </div>

          {/* Section 3: Attendance History */}
          <div className="mb-8 animate-fade-in-delay-2">
          <h3 className="text-xl font-semibold mb-4">Attendance History</h3>
          {attendanceHistory.length === 0 ? (
            <p className="text-gray-600">No records found.</p>
          ) : (
            <ul className="space-y-2">
              <li className="flex font-semibold text-gray-700">
                <span className="w-32">Date</span>
                <span className="w-40">Check-in</span>
                <span className="w-40">Check-out</span>
                <span className="w-32">Duration</span>
              </li>
                {attendanceHistory.map((entry, index) => (
                  <li 
                    key={entry.id || entry.date} 
                    className="bg-gray-50 p-3 rounded shadow text-left flex hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 animate-fade-in-up"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                  <span className="w-32">{entry.date}</span>
                  <span className="w-40">{entry.checkIn ? `✅ ${entry.checkIn}` : '❌ No Check-in'}</span>
                  <span className="w-40">{entry.checkOut ? `🕓 ${entry.checkOut}` : '⏳ No Check-out'}</span>
                  <span className="w-32">{entry.duration || '-'}</span>
                </li>
              ))}
            </ul>
            )}
          </div>

          {/* Section 4: Notes App (for interns only) */}
          {employee.role === 'intern' && (
            <div className="animate-fade-in-delay-3">
              <Notes userId={employee?.id} userName={employee?.fullName} />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default EmployeeDashboard;
